import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  CourseViewer,
  Enrollment,
  ModuleProgress,
  ModuleProgressStatus,
} from "@/modules/learningCenter/features/CourseDirectory/types/viewer";
import { getCourseViewer } from "@/modules/learningCenter/features/CourseDirectory/api/getCourseViewer";
import { enrollCourse } from "@/modules/learningCenter/features/CourseDirectory/api/enrollCourse";
import { unenrollCourse } from "@/modules/learningCenter/features/CourseDirectory/api/unenrollCourse";
import { reactivateEnrollment } from "@/modules/learningCenter/features/CourseDirectory/api/reactivateEnrollment";
import { completeModule as completeModuleApi } from "@/modules/learningCenter/features/CourseDirectory/api/completeModule";
import { resetModule as resetModuleApi } from "@/modules/learningCenter/features/CourseDirectory/api/resetModule";
import { updateLastAccessed as updateLastAccessedApi } from "@/modules/learningCenter/features/CourseDirectory/api/updateLastAccessed";

// An enrollment is "active" (the learner may take modules) when it exists and
// is not cancelled.
export function isEnrollmentActive(
  enrollment: Enrollment | null,
): enrollment is Enrollment {
  return enrollment !== null && enrollment.status !== "cancelled";
}

function setModuleStatus(
  moduleProgress: ModuleProgress[],
  moduleUuid: string,
  status: ModuleProgressStatus,
): ModuleProgress[] {
  const existing = moduleProgress.find((p) => p.moduleUuid === moduleUuid);
  if (existing) {
    return moduleProgress.map((p) =>
      p.moduleUuid === moduleUuid ? { ...p, status } : p,
    );
  }
  return [
    ...moduleProgress,
    {
      moduleUuid,
      status,
      startedAt: null,
      completedAt: null,
      lastAccessedAt: null,
    },
  ];
}

export interface UseCourseViewerResult {
  viewer: CourseViewer | null;
  isLoading: boolean;
  isMutating: boolean;
  /** True once the course reaches 100% as a result of a completeModule call. */
  justCompleted: boolean;
  acknowledgeCompletion: () => void;
  enroll: () => Promise<void>;
  unenroll: () => Promise<void>;
  reactivate: () => Promise<void>;
  completeModule: (moduleUuid: string) => Promise<void>;
  resetModule: (moduleUuid: string) => Promise<void>;
  setLastAccessed: (moduleUuid: string) => Promise<void>;
}

/**
 * Owns viewer state for a single course and exposes optimistic enrollment /
 * progress mutations with rollback. Error toasts are handled by the global
 * axios interceptor; on failure we restore the prior snapshot.
 */
export function useCourseViewer(courseUuid: string): UseCourseViewerResult {
  const [viewer, setViewer] = useState<CourseViewer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    getCourseViewer(courseUuid, controller.signal)
      .then((res) => setViewer(res))
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setViewer(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });
    return () => controller.abort();
  }, [courseUuid]);

  // Latest snapshot, so mutations can roll back without stale closures.
  const viewerRef = useRef(viewer);
  viewerRef.current = viewer;

  const enroll = useCallback(async () => {
    const snapshot = viewerRef.current;
    if (!snapshot) {
      return;
    }
    // Optimistic: synthesize an enrolled enrollment so the CTA flips to
    // "Continue" immediately.
    setViewer({
      ...snapshot,
      enrollment: {
        uuid: snapshot.enrollment?.uuid ?? "optimistic",
        status: "enrolled",
        progressPercentage: snapshot.enrollment?.progressPercentage ?? "0",
        enrolledAt: new Date().toISOString(),
        completedAt: null,
        cancelledAt: null,
        lastAccessedModuleUuid:
          snapshot.enrollment?.lastAccessedModuleUuid ?? null,
      },
    });
    setIsMutating(true);
    try {
      const enrollment = await enrollCourse(courseUuid);
      setViewer((prev) => (prev ? { ...prev, enrollment } : prev));
    } catch (err) {
      setViewer(snapshot);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, [courseUuid]);

  const unenroll = useCallback(async () => {
    const snapshot = viewerRef.current;
    if (!snapshot?.enrollment) {
      return;
    }
    const enrollmentUuid = snapshot.enrollment.uuid;
    setViewer({
      ...snapshot,
      enrollment: { ...snapshot.enrollment, status: "cancelled" },
    });
    setIsMutating(true);
    try {
      const enrollment = await unenrollCourse(enrollmentUuid);
      setViewer((prev) => (prev ? { ...prev, enrollment } : prev));
    } catch (err) {
      setViewer(snapshot);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const reactivate = useCallback(async () => {
    const snapshot = viewerRef.current;
    if (!snapshot?.enrollment) {
      return;
    }
    const enrollmentUuid = snapshot.enrollment.uuid;
    setViewer({
      ...snapshot,
      enrollment: { ...snapshot.enrollment, status: "enrolled" },
    });
    setIsMutating(true);
    try {
      const enrollment = await reactivateEnrollment(enrollmentUuid);
      setViewer((prev) => (prev ? { ...prev, enrollment } : prev));
    } catch (err) {
      setViewer(snapshot);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const completeModule = useCallback(async (moduleUuid: string) => {
    const snapshot = viewerRef.current;
    if (!snapshot?.enrollment) {
      return;
    }
    setViewer({
      ...snapshot,
      moduleProgress: setModuleStatus(
        snapshot.moduleProgress,
        moduleUuid,
        "completed",
      ),
    });
    setIsMutating(true);
    try {
      const enrollment = await completeModuleApi(moduleUuid);
      setViewer((prev) => (prev ? { ...prev, enrollment } : prev));
      if (enrollment.status === "completed") {
        setJustCompleted(true);
      }
    } catch (err) {
      setViewer(snapshot);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const resetModule = useCallback(async (moduleUuid: string) => {
    const snapshot = viewerRef.current;
    if (!snapshot?.enrollment) {
      return;
    }
    setViewer({
      ...snapshot,
      moduleProgress: setModuleStatus(
        snapshot.moduleProgress,
        moduleUuid,
        "not_started",
      ),
    });
    setIsMutating(true);
    try {
      const enrollment = await resetModuleApi(moduleUuid);
      setViewer((prev) => (prev ? { ...prev, enrollment } : prev));
    } catch (err) {
      setViewer(snapshot);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const setLastAccessed = useCallback(async (moduleUuid: string) => {
    const snapshot = viewerRef.current;
    if (!snapshot?.enrollment) {
      return;
    }
    const enrollmentUuid = snapshot.enrollment.uuid;
    // Optimistic pointer update; resume should reflect the latest module even
    // before the request settles.
    setViewer({
      ...snapshot,
      lastAccessedModuleUuid: moduleUuid,
      enrollment: {
        ...snapshot.enrollment,
        lastAccessedModuleUuid: moduleUuid,
      },
    });
    try {
      const enrollment = await updateLastAccessedApi(
        enrollmentUuid,
        moduleUuid,
      );
      setViewer((prev) =>
        prev
          ? {
              ...prev,
              enrollment,
              lastAccessedModuleUuid:
                enrollment.lastAccessedModuleUuid ?? moduleUuid,
            }
          : prev,
      );
    } catch (err) {
      setViewer(snapshot);
      throw err;
    }
  }, []);

  const acknowledgeCompletion = useCallback(() => {
    setJustCompleted(false);
  }, []);

  return {
    viewer,
    isLoading,
    isMutating,
    justCompleted,
    acknowledgeCompletion,
    enroll,
    unenroll,
    reactivate,
    completeModule,
    resetModule,
    setLastAccessed,
  };
}
